// One early listener retains browser install events while Firebase restores a session.
// Rapid Record owns a separate manifest and keeps its existing installation flow.
export const INSTALL_DISMISSED_KEY = "sbs-install-dismissed-v1";
const listeners = new Set();
let initialized = false;
let state = { standalone: false, apple: false, prompt: null, dismissed: false };

export function isAppleDevice(nav = navigator) {
  return /iPhone|iPad|iPod/i.test(nav.userAgent || "") ||
    (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
}

export function isStandalone(win = window) {
  return win.matchMedia?.("(display-mode: standalone)").matches === true ||
    win.navigator.standalone === true;
}

function publish(update) {
  state = { ...state, ...update };
  listeners.forEach((listener) => listener());
}

function readDismissed() {
  try { return window.localStorage.getItem(INSTALL_DISMISSED_KEY) === "1"; }
  catch { return state.dismissed; }
}

export function initializeWebAppInstall() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  publish({ standalone: isStandalone(), apple: isAppleDevice(), dismissed: readDismissed() });
  const media = window.matchMedia?.("(display-mode: standalone)");
  const refresh = () => publish({ standalone: isStandalone() });
  if (media?.addEventListener) media.addEventListener("change", refresh);
  else media?.addListener?.(refresh);
  window.addEventListener("pageshow", refresh);
  window.addEventListener("beforeinstallprompt", (event) => {
    const manifest = document.querySelector('link[rel="manifest"]')?.getAttribute("href") || "";
    if (window.location.pathname.startsWith("/rapid-record/") || manifest.includes("rapid-record.webmanifest")) {
      publish({ prompt: null });
      return;
    }
    event.preventDefault();
    publish({ prompt: event });
  });
  window.addEventListener("appinstalled", () => publish({ standalone: true, prompt: null }));
  window.addEventListener("storage", (event) => {
    if (event.key === INSTALL_DISMISSED_KEY || event.key === null) publish({ dismissed: readDismissed() });
  });
}

export function subscribeInstall(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export function getInstallSnapshot() { return state; }
export function dismissInstall() {
  try { window.localStorage.setItem(INSTALL_DISMISSED_KEY, "1"); } catch { /* Memory fallback for restricted storage. */ }
  publish({ dismissed: true });
}
export async function requestWebAppInstall() {
  const prompt = state.prompt;
  if (!prompt) return null;
  // A browser event is single-use, including after a cancellation or failure.
  publish({ prompt: null });
  await prompt.prompt();
  return prompt.userChoice;
}
